"use client";

import React, { useEffect, useState } from "react";
import "./styles.css";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  message,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import type { GlobalContextType } from "../../../../../../@types/global";
import { GlobalContext } from "../../../../../../context/globalContext";
import InputMask from "react-input-mask";
import type { ModalEditFornecedoresTypes } from "../../../../../../@types/modalFornecedores";

interface FormValues {
  nome: string;
  cnpj: string;
  email: string;
  telefone: string;
  type: string;
  files: any;
}

const ModalEditar: React.FC<ModalEditFornecedoresTypes> = ({
  editFornecedor,
  showFornecedores,
  showAddFornecedor,
  clientId,
}) => {
  const {
    postFornecedor,
    getFornecedores,
    getAnexo,
    userInfo,
    moduloMapeamento,
    moduloProcessos,
  } = React.useContext(GlobalContext) as GlobalContextType;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mask, setMask] = useState("(99) 99999-9999");

  // lista com itens a enviar (File) ou já salvos (com url)
  const [currentAnexos, setCurrentAnexos] = useState<any[]>([]);
  // anexos retornados do servidor (apenas para visual)
  const [anexos, setAnexos] = useState<any[]>([]);
  // controle do upload do antd
  const [contratoFileList, setContratoFileList] = useState<any[]>([]);

  // cria um item compatível com Upload.fileList
  const makeUploadItem = (file: File) => ({
    uid: `${Date.now()}-${file.name}`,
    name: file.name,
    status: "done",
    originFileObj: file,
  });

  // valida e adiciona arquivo (não envia automaticamente)
  const beforeUploadContrato = (file: any) => {
    console.log("[v0 DEBUG MODAL] beforeUploadContrato chamado", file);

    const allowed = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!allowed.includes(file.type)) {
      message.error("Apenas PDF, DOC ou DOCX.");
      return Upload.LIST_IGNORE;
    }
    if (file.size > 100 * 1024 * 1024) {
      message.error("Arquivo deve ter no máximo 100MB.");
      return Upload.LIST_IGNORE;
    }

    const realFile = file.originFileObj ? file.originFileObj : file;

    // atualiza fileList do componente Upload (visual)
    setContratoFileList([makeUploadItem(realFile)]);

    // mantém anexos já salvos (com url) e adiciona o novo File
    setCurrentAnexos((prev) => {
      const apenasSalvos = prev.filter((ax: any) => ax?.url);
      return [...apenasSalvos, realFile];
    });

    // impede upload automático do antd (vamos enviar junto no submit)
    return false;
  };

  // remove arquivo selecionado no Upload (apenas local)
  const onRemoveContrato = (file: any) => {
    const real = file.originFileObj ? file.originFileObj : file;
    setContratoFileList([]);

    setCurrentAnexos((prev) =>
      prev.filter((ax: any) => {
        // mantém arquivos que já estão salvos no backend (têm url)
        if (ax?.url) return true;

        const axFile = ax?.originFileObj ? ax.originFileObj : ax;
        const sameName = axFile?.name === real?.name;
        const sameSize = axFile?.size === real?.size;
        const sameLM =
          (axFile?.lastModified ?? 0) === (real?.lastModified ?? 0);
        return !(sameName && sameSize && sameLM);
      })
    );
  };

  const novoFornecedor = async (values: FormValues) => {
    console.log("[v0 DEBUG MODAL] novoFornecedor chamado");
    console.log("[v0 DEBUG MODAL] values:", values);
    console.log("[v0 DEBUG MODAL] currentAnexos:", currentAnexos);
    console.log("[v0 DEBUG MODAL] contratoFileList:", contratoFileList);

    setLoading(true);

    const valores: any = { ...values };

    // normalizo cnpj
    if (valores.cnpj) valores.cnpj = valores.cnpj.replace(/[^\d]/g, "");

    // se usuário tiver função que fixa a área, aplica
    const areaRef = userInfo?.funcoesDetalhadas?.find(
      (fd: any) =>
        fd.funcao_id?.name === "cadastrar-parceiro-fornecedor-area"
    )?.reference_model_id;
    if (areaRef) valores.area_id = areaRef;

    // separa anexos novos (sem url)
    const anexosNovos = currentAnexos.filter((ax: any) => !ax?.url);

    try {
      if (editFornecedor) {
        // --- EDIÇÃO ---
        console.log("[v0 DEBUG MODAL] Modo EDIÇÃO", editFornecedor._id);
        const { nome, email, cnpj, type } = valores;
        if (!nome || !email || !cnpj || !type) {
          message.error("Preencha todos os campos!");
          setLoading(false);
          return;
        }

        // garante enviar _id para o backend
        const payload = { ...valores, _id: editFornecedor._id };

        // postFornecedor usa assinatura: (values, currentAnexos, clientId)
        const res = await postFornecedor(payload, anexosNovos, clientId);

        console.log("[v0 DEBUG MODAL] postFornecedor (edição) retornou:", res);

        // atualiza lista de fornecedores e anexos
        clientId && getFornecedores(clientId);

        // busca anexos do fornecedor atualizado e atualiza estado
        try {
          const anexosAtualizados = await getAnexo(editFornecedor._id);
          setAnexos(anexosAtualizados || []);
          setCurrentAnexos(anexosAtualizados || []);
        } catch (e) {
          console.warn("[v0 DEBUG MODAL] Falha ao buscar anexos após edição", e);
        }

        message.success("Fornecedor atualizado com sucesso!");
      } else {
        // --- CRIAÇÃO ---
        console.log("[v0 DEBUG MODAL] Modo CRIAÇÃO");
        const { nome, email, cnpj, type } = valores;
        if (!nome || !email || !cnpj || !type) {
          message.error("Preencha todos os campos!");
          setLoading(false);
          return;
        }

        // chama postFornecedor (criação)
        const res = await postFornecedor(valores, anexosNovos, clientId);
        console.log("[v0 DEBUG MODAL] postFornecedor (criação) retornou:", res);

        // refresh lista de fornecedores
        clientId && getFornecedores(clientId);

        // tenta extrair o id do fornecedor criado (backend retorna fornecedor_id em /fornecedoresmodel)
        const newFornecedorId =
          (res && (res.fornecedor_id || res.fornecedor_id === 0 ? res.fornecedor_id : undefined)) ||
          (res && res.fornecedor && res.fornecedor._id) ||
          null;

        if (newFornecedorId) {
          try {
            const anexosCriados = await getAnexo(newFornecedorId);
            setAnexos(anexosCriados || []);
            setCurrentAnexos(anexosCriados || []);
          } catch (e) {
            console.warn("[v0 DEBUG MODAL] Falha ao buscar anexos após criação", e);
          }
        }

        message.success("Fornecedor criado com sucesso!");
      }

      // limpa form e fecha modal
      form.resetFields();
      setCurrentAnexos([]);
      setContratoFileList([]);
      showAddFornecedor();
    } catch (err) {
      console.error("[v0 DEBUG MODAL] Erro ao salvar fornecedor:", err);
      message.error("Erro ao salvar fornecedor.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (editFornecedor && showFornecedores) {
      console.log("[v0 DEBUG MODAL] useEffect - Editando fornecedor:", editFornecedor._id);

      form.setFieldsValue({
        nome: editFornecedor.nome,
        cnpj: editFornecedor.cnpj,
        telefone: editFornecedor.telefone,
        email: editFornecedor.email,
        type: editFornecedor.type,
        area_id: (editFornecedor as any).area_id,
        representante: (editFornecedor as any).representante,
      });

      setLoading(true);
      getAnexo(editFornecedor._id)
        .then((res: any) => {
          console.log("[v0 DEBUG MODAL] Anexos recebidos:", res);
          setAnexos(res || []);
          // mantemos os anexos no currentAnexos para exibí-los/evitar re-upload
          setCurrentAnexos(res || []);
          // se houver arquivos já salvos, populamos o fileList para visual (apenas o primeiro)
          if (res && res.length > 0) {
            const first = res[0];
            setContratoFileList([
              {
                uid: `${first._id}`,
                name: first.name || "Arquivo",
                status: "done",
                url: first.url,
              },
            ]);
          } else {
            setContratoFileList([]);
          }
        })
        .catch((e) => {
          console.warn("[v0 DEBUG MODAL] getAnexo error:", e);
          setAnexos([]);
          setCurrentAnexos([]);
          setContratoFileList([]);
        })
        .finally(() => setLoading(false));
    } else {
      form.resetFields();
      setCurrentAnexos([]);
      setContratoFileList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFornecedores, editFornecedor]);

  return (
    <Modal
      title={editFornecedor ? "Editar fornecedor" : "Adicionar fornecedor"}
      open={showFornecedores}
      footer={[]}
      onCancel={showAddFornecedor}
      width={700}
    >
      <Form
        layout="vertical"
        form={form}
        name="adiciona_fornecedor"
        onFinish={novoFornecedor}
        autoComplete="off"
        style={{
          marginTop: "2rem",
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: "0.5rem",
        }}
      >
        <Form.Item label="Razão social" name="nome" required>
          <Input type="text" />
        </Form.Item>

        <Form.Item label="Nome do responsável" name="representante" required>
          <Input type="text" />
        </Form.Item>

        <Form.Item label="CNPJ" name="cnpj" required>
          <InputMask type="text" mask="99.999.999/9999-99">
            <Input />
          </InputMask>
        </Form.Item>

        <Form.Item label="E-mail" name="email" required>
          <Input type="email" />
        </Form.Item>

        <Form.Item label="Telefone" name="telefone">
          <InputMask
            type="text"
            mask={mask}
            onBlur={(e: any) => {
              if (e.target.value.replace("_", "").length === 14) {
                setMask("(99) 9999-9999");
              }
            }}
            onFocus={(e: any) => {
              if (e.target.value.replace("_", "").length === 14) {
                setMask("(99) 99999-9999");
              }
            }}
          >
            <Input />
          </InputMask>
        </Form.Item>

        <Form.Item label="Fornecedor" name="type" required>
          <Radio.Group
            options={[
              { label: "Novo", value: "novo" },
              { label: "Vigente", value: "vigente" },
            ]}
            optionType="button"
          />
        </Form.Item>

        {userInfo?.grupos?.find(
          (gp: { name: string }) =>
            gp.name === "seusdados" ||
            gp.name === "assinante" ||
            gp.name === "sponsor"
        ) && (
          <Form.Item label="Área" name="area_id">
            {moduloMapeamento.currentMapeamento?.areas?.[0] ? (
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder="Selecione uma área"
                options={moduloMapeamento.currentMapeamento?.areas
                  ?.map((a) =>
                    moduloProcessos?.areas?.find((area) => area._id === a)
                  )
                  ?.filter((a) => a?._id && a?.description)
                  ?.map((a) => ({
                    value: a?._id,
                    label: a?.description,
                  }))}
                filterOption={(input, option) =>
                  (option?.label?.toLowerCase() ?? "").includes(
                    input.toLowerCase()
                  )
                }
                onChange={(value) => {
                  form.setFieldsValue({ area_id: value });
                }}
              />
            ) : (
              <Alert type="warning" showIcon message="Cliente sem áreas" />
            )}
          </Form.Item>
        )}

        {/* campo novo: upload do contrato */}
        <Form.Item label="Contrato com o Fornecedor">
          <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.85 }}>
            Faça aqui o upload do contrato que você possui com o fornecedor
          </div>
          <Upload
            beforeUpload={beforeUploadContrato}
            onRemove={onRemoveContrato}
            fileList={contratoFileList}
            maxCount={1}
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          >
            <Button icon={<UploadOutlined />}>Selecionar arquivo</Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            className="btn-success"
            loading={loading}
            htmlType="submit"
          >
            Salvar
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ModalEditar;
